import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { useTranslations } from 'use-intl';
import { useSupabaseStore } from '@/pages/CustomerSupport/supabaseStore';
import { useSupabaseAuth } from '@/pages/CustomerSupport/useSupabaseAuth';
import { Roles } from '@/pages/ClientProfiles/Roles.types';
import {
	AnalysisConfiguration,
	AnalysisType,
	ANALYSIS_TYPE_LABELS,
	OutputSchemaField
} from '@/types/AnalysisConfiguration';
import { useOrganizationId } from '@/pages/CustomerSupport/useOrganizationId';
import { VersionHistoryPanel } from '@/components/VersionHistoryPanel';

const ORGANIZATION_ID = import.meta.env.VITE_ORGANIZATION_ID || '';

const formatOutputSchema = (outputSchema: Record<string, OutputSchemaField> | null): string => {
	if (!outputSchema) return '{}';

	const formatted: Record<string, any> = {};
	Object.entries(outputSchema).forEach(([key, field]) => {
		const fieldInfo: any = {
			type: field.type
		};
		if (field.range) fieldInfo.range = field.range;
		if (field.description) fieldInfo.description = field.description;
		if (field.conditional) fieldInfo.conditional = field.conditional;
		if (field.values) fieldInfo.values = field.values;
		formatted[key] = fieldInfo;
	});

	return JSON.stringify(formatted, null, 2);
};

export const ManageAnalysisConfigurations: React.FC = () => {
	const t = useTranslations('ManageAnalysisConfigurations');
	const { isLoading: authLoading } = useSupabaseAuth(Roles.KOGNI_ADMIN);
	const { organizationId } = useOrganizationId(authLoading);
	const supabase = useSupabaseStore((state) => state.supabase);
	const finalOrganizationId = organizationId || ORGANIZATION_ID;
	const [configurations, setConfigurations] = useState<AnalysisConfiguration[]>([]);
	const [selectedType, setSelectedType] = useState<AnalysisType | null>(null);
	const [instructions, setInstructions] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hasChanges, setHasChanges] = useState(false);
	const [isInputSchemaExpanded, setIsInputSchemaExpanded] = useState(true);
	const [isOutputSchemaExpanded, setIsOutputSchemaExpanded] = useState(true);
	const [showPreview, setShowPreview] = useState(false);

	const loadConfigurations = useCallback(async () => {
		if (!finalOrganizationId) {
			setError(t('OrganizationIdNotFound'));
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const { data, error: fetchError } = await supabase
				.from('organization_analysis_configurations')
				.select('*')
				.eq('organization_id', finalOrganizationId)
				.order('analysis_type', { ascending: true });

			if (fetchError) {
				throw fetchError;
			}

			if (data) {
				setConfigurations(data);
				if (data.length > 0 && !selectedType) {
					setSelectedType(data[0].analysis_type);
					setInstructions(data[0].instructions);
				}
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : t('FailedToLoadConfigurations'));
		} finally {
			setIsLoading(false);
		}
	}, [finalOrganizationId, supabase, t, selectedType]);

	useEffect(() => {
		if (!authLoading && finalOrganizationId) {
			loadConfigurations();
		}
	}, [authLoading, finalOrganizationId, loadConfigurations]);

	const handleSelectType = (type: AnalysisType) => {
		if (hasChanges) {
			if (!confirm(t('UnsavedChangesConfirm'))) {
				return;
			}
		}
		setSelectedType(type);
		const config = configurations.find((c) => c.analysis_type === type);
		setInstructions(config?.instructions || '');
		setHasChanges(false);
		setShowPreview(false);
	};

	const handleSave = async () => {
		if (!selectedType || !finalOrganizationId) {
			console.error('Missing required data:', { selectedType, finalOrganizationId });
			return;
		}

		setIsSaving(true);
		setError(null);

		try {
			const {
				data: { session }
			} = await supabase.auth.getSession();

			if (!session) {
				throw new Error('Not authenticated');
			}

			const { data, error } = await supabase.functions.invoke('update-analysis-instructions', {
				body: {
					organization_id: finalOrganizationId,
					analysis_type: selectedType,
					instructions
				}
			});

			if (error) {
				throw error;
			}

			if (data?.success === false) {
				throw new Error(data.error || t('FailedToSaveInstructions'));
			}

			setHasChanges(false);
			await loadConfigurations();
		} catch (err) {
			console.error('Save error:', err);
			setError(err instanceof Error ? err.message : t('FailedToSaveInstructions'));
		} finally {
			setIsSaving(false);
		}
	};

	const generatePreview = () => {
		if (!selectedConfig) return '';

		let preview = '';

		if (selectedConfig.input_schema) {
			preview += '## Available Data Variables\n\n';

			if (
				selectedConfig.input_schema.variables &&
				selectedConfig.input_schema.variables.length > 0
			) {
				selectedConfig.input_schema.variables.forEach((variable) => {
					preview += `### ${variable.name}\n`;
					preview += `- **Type**: ${variable.type}\n`;
					if (variable.range) preview += `- **Range**: ${variable.range}\n`;
					if (variable.values) preview += `- **Values**: ${variable.values.join(', ')}\n`;
					if (variable.description) preview += `- **Description**: ${variable.description}\n`;
					if (variable.source) preview += `- **Source**: ${variable.source}\n`;
					if (variable.conditional) preview += `- **Conditional**: ${variable.conditional}\n`;
					preview += '\n';
				});
			} else if (
				selectedConfig.input_schema.fields &&
				selectedConfig.input_schema.fields.length > 0
			) {
				preview += 'Fields: ' + selectedConfig.input_schema.fields.join(', ') + '\n\n';
				if (selectedConfig.input_schema.description) {
					preview += `**Description**: ${selectedConfig.input_schema.description}\n\n`;
				}
				if (selectedConfig.input_schema.source) {
					preview += `**Source**: ${selectedConfig.input_schema.source}\n\n`;
				}
			}
		}

		if (selectedConfig.output_schema) {
			preview += '## Required Output Format\n\n';
			preview += '```json\n';
			preview += formatOutputSchema(selectedConfig.output_schema);
			preview += '\n```\n\n';
		}

		preview += '## Instructions\n\n';
		preview += instructions;

		return preview;
	};

	const handleInstructionsChange = (value: string) => {
		setInstructions(value);
		setHasChanges(true);
	};

	const selectedConfig = configurations.find((c) => c.analysis_type === selectedType);

	if (authLoading || isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Typography type="h3">{t('Loading')}</Typography>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 p-8">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<div className="mb-2">
						<Typography type="h1" weight="bold">
							{t('Title')}
						</Typography>
					</div>
					<div>
						<Typography type="text" color="subtitle" className="text-base">
							{t('Subtitle')}
						</Typography>
					</div>
				</div>

				{error && (
					<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
						<Typography type="text" color="error">
							{error}
						</Typography>
					</div>
				)}

				<div className="grid grid-cols-12 gap-4">
					<div className="col-span-2">
						<div className="bg-white rounded-lg shadow-sm p-4 sticky top-8">
							<Typography type="h3" weight="semibold" className="mb-4 text-sm">
								{t('AnalysisTypes')}
							</Typography>
							<div className="space-y-1.5">
								{configurations.map((config) => (
									<button
										key={config.analysis_type}
										onClick={() => handleSelectType(config.analysis_type)}
										className={`w-full text-left p-2.5 rounded-md transition-colors ${
											selectedType === config.analysis_type
												? 'bg-blue-50 border border-blue-500'
												: 'bg-gray-50 border border-transparent hover:bg-gray-100'
										}`}
									>
										<Typography
											type="text"
											weight={selectedType === config.analysis_type ? 'semibold' : 'normal'}
											className="text-xs leading-tight"
										>
											{ANALYSIS_TYPE_LABELS[config.analysis_type]}
										</Typography>
									</button>
								))}
							</div>
						</div>
					</div>

					<div className="col-span-10">
						{selectedConfig ? (
							<div className="space-y-6">
								<div className="bg-white rounded-lg shadow-sm p-8">
									<div className="mb-8">
										<div className="mb-2">
											<Typography type="h2" weight="bold">
												{ANALYSIS_TYPE_LABELS[selectedConfig.analysis_type]}
											</Typography>
										</div>
										<div>
											<Typography
												type="text"
												color="subtitle"
												size="sm"
												className="leading-relaxed"
											>
												{t('EditInstructionsSubtitle')}
											</Typography>
										</div>
									</div>

									{selectedConfig.input_schema && (
										<div className="mb-8 pb-8 border-b border-gray-200">
											<button
												onClick={() => setIsInputSchemaExpanded(!isInputSchemaExpanded)}
												className="w-full flex items-center justify-between mb-4 text-left hover:opacity-80 transition-opacity"
											>
												<div className="flex items-center gap-2">
													<Typography type="h3" weight="semibold" className="text-gray-700">
														{t('AvailableDataVariables')}
													</Typography>
													<span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-600 rounded border border-blue-200">
														{t('ReadOnly')}
													</span>
												</div>
												<svg
													className={`w-5 h-5 text-gray-500 transition-transform ${
														isInputSchemaExpanded ? 'rotate-180' : ''
													}`}
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M19 9l-7 7-7-7"
													/>
												</svg>
											</button>

											{isInputSchemaExpanded && (
												<div className="space-y-6">
													{(selectedConfig.input_schema.variables &&
														selectedConfig.input_schema.variables.length > 0) ||
													(selectedConfig.input_schema.fields &&
														selectedConfig.input_schema.fields.length > 0) ? (
														<div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
															<div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
																<Typography
																	type="text"
																	weight="semibold"
																	size="xs"
																	className="text-gray-700"
																>
																	{t('AvailableVariables')}
																</Typography>
															</div>
															<div className="overflow-x-auto">
																<table className="w-full text-xs">
																	<thead className="bg-gray-50">
																		<tr>
																			<th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-200">
																				{t('FieldName')}
																			</th>
																			{selectedConfig.input_schema.variables &&
																				selectedConfig.input_schema.variables.length > 0 && (
																					<>
																						<th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-200">
																							{t('Type')}
																						</th>
																						<th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-200">
																							{t('RangeValues')}
																						</th>
																						<th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-200">
																							{t('Description')}
																						</th>
																						<th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-200">
																							{t('WhereDoesItComeFrom')}
																						</th>
																					</>
																				)}
																		</tr>
																	</thead>
																	<tbody className="bg-white divide-y divide-gray-100">
																		{selectedConfig.input_schema.variables &&
																		selectedConfig.input_schema.variables.length > 0
																			? selectedConfig.input_schema.variables.map(
																					(variable, index) => (
																						<tr
																							key={variable.name}
																							className={
																								index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
																							}
																						>
																							<td className="px-2 py-1.5 whitespace-nowrap">
																								<div className="flex items-center gap-1">
																									<span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200 whitespace-nowrap">
																										{variable.name}
																									</span>
																									{variable.conditional && (
																										<span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap">
																											{variable.conditional}
																										</span>
																									)}
																								</div>
																							</td>
																							<td className="px-2 py-1.5">
																								<span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
																									{variable.type}
																								</span>
																							</td>
																							<td className="px-2 py-1.5">
																								<div className="flex flex-wrap gap-1">
																									{variable.range && (
																										<span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
																											{variable.range}
																										</span>
																									)}
																									{variable.values &&
																										variable.values.map((val, idx) => (
																											<span
																												key={idx}
																												className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200"
																											>
																												{val}
																											</span>
																										))}
																									{!variable.range && !variable.values && (
																										<span className="text-xs text-gray-400">—</span>
																									)}
																								</div>
																							</td>
																							<td className="px-2 py-1.5">
																								<Typography
																									type="text"
																									size="xs"
																									className="text-gray-600 leading-tight"
																								>
																									{variable.description}
																								</Typography>
																							</td>
																							<td className="px-2 py-1.5">
																								<Typography
																									type="text"
																									size="xs"
																									className="text-gray-600 leading-tight bg-gray-50 px-1.5 py-1 rounded border border-gray-200 font-mono"
																								>
																									{variable.source}
																								</Typography>
																							</td>
																						</tr>
																					)
																				)
																			: selectedConfig.input_schema.fields &&
																				  selectedConfig.input_schema.fields.length > 0
																				? selectedConfig.input_schema.fields.map((field, index) => (
																						<tr
																							key={index}
																							className={
																								index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
																							}
																						>
																							<td className="px-2 py-1.5">
																								<span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200 whitespace-nowrap">
																									{field}
																								</span>
																							</td>
																						</tr>
																					))
																				: null}
																	</tbody>
																</table>
															</div>
														</div>
													) : (
														<div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
															<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
																<div className="space-y-1">
																	<div className="flex items-center gap-2 mb-2">
																		<div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
																			<span className="text-blue-600 text-sm">📋</span>
																		</div>
																		<Typography
																			type="text"
																			weight="semibold"
																			size="sm"
																			className="text-gray-700"
																		>
																			{t('WhatIsThisData')}
																		</Typography>
																	</div>
																	<Typography
																		type="text"
																		size="sm"
																		className="text-gray-600 leading-relaxed pl-10"
																	>
																		{selectedConfig.input_schema.description}
																	</Typography>
																</div>

																<div className="space-y-1">
																	<div className="flex items-center gap-2 mb-2">
																		<div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
																			<span className="text-purple-600 text-sm">📍</span>
																		</div>
																		<Typography
																			type="text"
																			weight="semibold"
																			size="sm"
																			className="text-gray-700"
																		>
																			{t('WhereDoesItComeFrom')}
																		</Typography>
																	</div>
																	<div className="pl-10">
																		<Typography
																			type="text"
																			size="sm"
																			className="text-gray-600 leading-relaxed bg-gray-50 px-3 py-2 rounded-md border border-gray-200 font-mono text-xs"
																		>
																			{selectedConfig.input_schema.source}
																		</Typography>
																	</div>
																</div>

																<div className="space-y-1 md:col-span-2">
																	<div className="flex items-center gap-2 mb-2">
																		<div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
																			<span className="text-green-600 text-sm">🗂️</span>
																		</div>
																		<Typography
																			type="text"
																			weight="semibold"
																			size="sm"
																			className="text-gray-700"
																		>
																			{t('DataFormat')}
																		</Typography>
																	</div>
																	<Typography
																		type="text"
																		size="sm"
																		className="text-gray-600 leading-relaxed pl-10"
																	>
																		{selectedConfig.input_schema.structure}
																	</Typography>
																</div>
															</div>
														</div>
													)}

													<div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
														<Typography
															type="text"
															size="xs"
															className="text-amber-800 leading-relaxed"
														>
															{t('VariablesDescription')}
														</Typography>
													</div>
												</div>
											)}
										</div>
									)}

									<div className="mb-8">
										<div className="flex items-center justify-between mb-4">
											<Typography type="h3" weight="semibold" className="text-gray-700">
												{t('AIAgentInstructions')}
											</Typography>
											<Button
												color="secondary"
												size="small"
												onClick={() => setShowPreview(!showPreview)}
												label={showPreview ? t('HidePreview') : t('ShowPreview')}
											/>
										</div>
										<TextArea
											name="instructions"
											label=""
											value={instructions}
											onChange={(e) => handleInstructionsChange(e.target.value)}
											rows={25}
											disabled={isSaving}
											className="font-mono text-sm leading-relaxed"
										/>
									</div>

									<VersionHistoryPanel entityId={selectedConfig.id} contentKey="instructions" />

									{showPreview && (
										<div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
											<div className="flex items-center gap-2 mb-4">
												<span className="text-2xl">👁️</span>
												<div>
													<Typography type="h3" weight="semibold" className="text-gray-900">
														{t('Preview')}
													</Typography>
													<Typography type="text" size="sm" className="text-gray-600">
														This is how the complete prompt will be sent to the AI agent
													</Typography>
												</div>
											</div>
											<div className="bg-white rounded-lg p-6 border border-gray-300 shadow-sm max-h-[600px] overflow-y-auto">
												<div className="prose prose-sm max-w-none">
													<pre className="text-xs font-mono text-gray-800 leading-relaxed whitespace-pre-wrap bg-gray-50 p-4 rounded border border-gray-200">
														{generatePreview()}
													</pre>
												</div>
											</div>
										</div>
									)}

									{selectedConfig.output_schema && (
										<div className="mb-8">
											<button
												onClick={() => setIsOutputSchemaExpanded(!isOutputSchemaExpanded)}
												className="w-full flex items-center justify-between mb-4 text-left hover:opacity-80 transition-opacity"
											>
												<Typography type="h3" weight="semibold" className="text-gray-700">
													{t('RequiredOutputFormat')}
												</Typography>
												<svg
													className={`w-5 h-5 text-gray-500 transition-transform ${
														isOutputSchemaExpanded ? 'rotate-180' : ''
													}`}
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M19 9l-7 7-7-7"
													/>
												</svg>
											</button>
											{isOutputSchemaExpanded && (
												<div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
													<pre className="text-xs font-mono text-green-400 leading-relaxed">
														{formatOutputSchema(selectedConfig.output_schema)}
													</pre>
												</div>
											)}
										</div>
									)}

									<div className="flex items-center justify-between pt-6 border-t border-gray-200 sticky bottom-0 bg-white z-10">
										<Typography type="text" size="sm" color="subtitle" className="leading-relaxed">
											{t('LastUpdated')}: {new Date(selectedConfig.updated_at).toLocaleString()}
										</Typography>
										<div className="flex gap-3">
											{hasChanges && (
												<Button
													color="secondary"
													size="auto"
													onClick={() => {
														setInstructions(selectedConfig.instructions);
														setHasChanges(false);
														setShowPreview(false);
													}}
													disabled={isSaving}
													label={t('Cancel')}
												/>
											)}
											<Button
												color="primary"
												size="auto"
												onClick={handleSave}
												disabled={!hasChanges || isSaving}
												label={isSaving ? t('Saving') : t('SaveChanges')}
											/>
										</div>
									</div>
								</div>
							</div>
						) : (
							<div className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-center min-h-[500px]">
								<Typography type="text" color="subtitle">
									{t('SelectTypeToEdit')}
								</Typography>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
